/**
 * PATH: packages/ui/src/components/settings/sender-profiles/SenderProfileRow.tsx
 * FILE_ID: UI-SETTINGS-SENDERS-ROW-027
 * PURPOSE: One sender-domain stewardship row — domain + ranked role pills
 *          with accept / correct / reject verdicts.
 * WHY: Verdicts must be one tap: MDM stewardship queues die when
 *      correction costs more than ignoring a wrong label.
 * LANDMINE: roles[0] IS the classifier's prior (backend ranks with the
 *      same rule the prior uses) — never re-order client-side.
 * RELATED: UI-SETTINGS-SENDERS-027 (section owns state + API calls).
 */

import { useState } from 'react'
import { Check, ChevronDown, Loader2, UsersRound, X } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { SenderProfile, SenderProfileRole, SenderRoleOption } from './types'
import { SenderCompanyEditor } from './SenderCompanyEditor'

/** Muted tint per evidence source — stronger evidence, stronger ink. */
const EVIDENCE_LABEL: Record<string, string> = {
  org_member: 'organisation member',
  feedback: 'operator feedback',
  xero_ledger: 'Xero ledger',
  crm: 'CRM record',
  label_votes: 'classified email history',
  seed_b0: 'legacy classifier history',
}

function roleTitle(r: SenderProfileRole): string {
  const source = EVIDENCE_LABEL[r.evidence_source] || r.evidence_source || 'observations'
  const pct = Math.round((r.confidence || 0) * 100)
  return (
    `${r.role_label}: from ${source}` +
    `${pct > 0 ? `, ${pct}% confidence` : ''}` +
    ` (${r.observation_count} observation${r.observation_count === 1 ? '' : 's'})`
  )
}

export interface SenderProfileRowProps {
  profile: SenderProfile
  roleOptions: SenderRoleOption[]
  /** Which verdict is in flight: `${domain}:${role}:${kind}` or null. */
  busyKey: string | null
  onVerdict: (
    kind: 'accept' | 'correct' | 'reject',
    role: string,
    chosenRole?: string,
  ) => void
  /** 029 — persist a corrected/attached company name for this domain. */
  onCompanySave?: (clientName: string) => void
  /** W3-V8 — people cross-nest: expanded state + toggle (both or neither). */
  peopleExpanded?: boolean
  onTogglePeople?: () => void
}

export function SenderProfileRow({
  profile,
  roleOptions,
  busyKey,
  onVerdict,
  onCompanySave,
  peopleExpanded,
  onTogglePeople,
}: SenderProfileRowProps) {
  const [correcting, setCorrecting] = useState(false)
  const primary = profile.roles[0]

  return (
    <div className="px-3 py-2.5 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
              {profile.domain}
            </span>
            {profile.roles.map((r, i) => (
              <span
                key={r.role}
                title={roleTitle(r)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]',
                  i === 0
                    ? 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-500/30 font-medium'
                    : 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700',
                  r.vetoed && 'line-through opacity-60',
                )}
              >
                {r.role_label}
              </span>
            ))}
          </div>
          {profile.senders.length > 0 && (
            <p className="text-xs text-slate-400 truncate mt-0.5">
              {profile.senders.slice(0, 3).join(', ')}
              {profile.senders.length > 3 ? ` +${profile.senders.length - 3} more` : ''}
            </p>
          )}
          {onCompanySave && (
            <SenderCompanyEditor
              domain={profile.domain}
              company={profile.company}
              busy={busyKey === `${profile.domain}:company`}
              onSave={onCompanySave}
            />
          )}
        </div>

        {/* Verdicts on the PRIMARY role — the one the classifier will use. */}
        {primary && (
          <div className="flex items-center gap-1 shrink-0">
            {onTogglePeople && (
              <button
                onClick={onTogglePeople}
                title="People at this domain"
                aria-label={`Show people at ${profile.domain}`}
                aria-expanded={peopleExpanded}
                className={cn(
                  'p-1.5 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800',
                  peopleExpanded && 'bg-slate-100 dark:bg-slate-800 text-teal-600 dark:text-teal-300',
                )}
              >
                <UsersRound className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => onVerdict('accept', primary.role)}
              disabled={busyKey != null}
              title={`Confirm ${primary.role_label}`}
              aria-label={`Confirm ${primary.role_label} for ${profile.domain}`}
              className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-40"
            >
              {busyKey === `${profile.domain}:${primary.role}:accept` ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5" />
              )}
            </button>
            <button
              onClick={() => setCorrecting((v) => !v)}
              disabled={busyKey != null}
              title="Correct the role"
              aria-label={`Correct role for ${profile.domain}`}
              className={cn(
                'p-1.5 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40',
                correcting && 'bg-slate-100 dark:bg-slate-800',
              )}
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onVerdict('reject', primary.role)}
              disabled={busyKey != null}
              title={`Reject ${primary.role_label} (veto)`}
              aria-label={`Reject ${primary.role_label} for ${profile.domain}`}
              className="p-1.5 rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
            >
              {busyKey === `${profile.domain}:${primary.role}:reject` ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <X className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Correction menu — pick the role this sender SHOULD carry. */}
      {correcting && primary && (
        <div className="flex flex-wrap gap-1.5 mt-2 pl-1">
          {roleOptions
            .filter((o) => o.role !== primary.role)
            .map((o) => (
              <button
                key={o.role}
                onClick={() => {
                  setCorrecting(false)
                  onVerdict('correct', primary.role, o.role)
                }}
                disabled={busyKey != null}
                className="px-2 py-1 rounded-full border border-slate-200 dark:border-slate-700 text-[11px] text-slate-600 dark:text-slate-300 hover:border-teal-400 hover:text-teal-700 dark:hover:text-teal-300 disabled:opacity-40"
              >
                {o.label}
              </button>
            ))}
        </div>
      )}
    </div>
  )
}
